import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { ControllersWrapper } from '@components/form/style';
import { BusinessAddress, BusinessInfo, SellingAccount } from '@models/account';
import BusinessAddressDropdown from './BusinessAddressDropdown';
import size from 'lodash/size';
import get from 'lodash/get';
import find from 'lodash/find';
import BusinessDetailsControllers from '@features/account-settings/components/BusinessDetailsControllers';
import {
  BUSINESS_DETAILS_CONTROLLER_GROUP,
  BUSINESS_DETAILS_PHONE_FIELD_CONTROLLER_NAME,
  BUSINESS_DETAILS_COUNTRY_FIELD_CONTROLLER_NAME,
  BUSINESS_DETAILS_ADDRESS_LINE1_FIELD_CONTROLLER_NAME,
  BUSINESS_DETAILS_ADDRESS_LINE2_FIELD_CONTROLLER_NAME,
  BUSINESS_DETAILS_CITY_FIELD_CONTROLLER_NAME,
  BUSINESS_DETAILS_STATE_FIELD_CONTROLLER_NAME,
  BUSINESS_DETAILS_POSTAL_CODE_FIELD_CONTROLLER_NAME,
  SELECTED_ADDRESS_ID,
  SELECTED_SELLING_ACCOUNT_ID,
} from '@features/account-settings/constants';
import {
  GROCERY_CENTRAL_PORTAL,
  PRE_POPULATION_SUCCESS,
  SELLING_ACCOUNTS,
} from 'src/pages/retailerAccountRegistration/constants';
import { logEvent } from '@utils/takt';

import { getSelectedSellingAccount } from '@features/account-registration/utils';

const logWfmPrePopulationSuccess = (
  sellingAccounts: SellingAccount[],
  selectedSellingAccountId: string,
  fieldsToUpdate: Array<[string, string | undefined]>,
) => {
  const sellingAccount = getSelectedSellingAccount(
    sellingAccounts,
    selectedSellingAccountId,
  );
  if (sellingAccount?.portals?.includes(GROCERY_CENTRAL_PORTAL)) {
    const populatedFields = fieldsToUpdate
      .filter(([name]) => name !== SELECTED_ADDRESS_ID)
      .reduce((acc, [name, value]) => ({ ...acc, [name]: !!value }), {});
    logEvent(PRE_POPULATION_SUCCESS, populatedFields);
  }
};

const BusinessDetailsEditor = () => {
  const { getValues, setValue, trigger, watch } = useFormContext();

  const business = getValues(BUSINESS_DETAILS_CONTROLLER_GROUP) as BusinessInfo;

  const selectedAddressId = watch(SELECTED_ADDRESS_ID);
  let selectedAddress: BusinessAddress | null | undefined = null;
  if (business?.addresses) {
    selectedAddress =
      business.addresses.length == 1
        ? get(business.addresses, 0)
        : selectedAddressId
        ? find(business.addresses, { addressToken: selectedAddressId })
        : null;
  }

  useEffect(() => {
    if (business.addresses && selectedAddress != null) {
      // Set values from the address in form state
      const fieldsToUpdate: Array<[string, string | undefined]> = [
        [SELECTED_ADDRESS_ID, selectedAddress.addressToken],
        [
          BUSINESS_DETAILS_PHONE_FIELD_CONTROLLER_NAME,
          selectedAddress.phoneNumber,
        ],
        [
          BUSINESS_DETAILS_COUNTRY_FIELD_CONTROLLER_NAME,
          selectedAddress.countryCode,
        ],
        [
          BUSINESS_DETAILS_ADDRESS_LINE1_FIELD_CONTROLLER_NAME,
          selectedAddress.addressLine1,
        ],
        [
          BUSINESS_DETAILS_ADDRESS_LINE2_FIELD_CONTROLLER_NAME,
          selectedAddress.addressLine2,
        ],
        [BUSINESS_DETAILS_CITY_FIELD_CONTROLLER_NAME, selectedAddress.city],
        [BUSINESS_DETAILS_STATE_FIELD_CONTROLLER_NAME, selectedAddress.state],
        [
          BUSINESS_DETAILS_POSTAL_CODE_FIELD_CONTROLLER_NAME,
          selectedAddress.zipCode,
        ],
      ];
      fieldsToUpdate.forEach(([fieldName, fieldValue]) => {
        setValue(fieldName, fieldValue);
      });

      fieldsToUpdate.forEach(([fieldName]) => {
        trigger(fieldName);
      });

      logWfmPrePopulationSuccess(
        getValues(SELLING_ACCOUNTS) as SellingAccount[],
        getValues(SELECTED_SELLING_ACCOUNT_ID) as string,
        fieldsToUpdate,
      );
    }
  }, [business.addresses, setValue, trigger, selectedAddress]);

  return (
    <ControllersWrapper>
      {selectedAddress == null &&
        business.addresses &&
        size(business.addresses) > 1 && (
          <BusinessAddressDropdown businessAddresses={business.addresses} />
        )}
      {(selectedAddress || size(business.addresses) == 0) && (
        <BusinessDetailsControllers />
      )}
    </ControllersWrapper>
  );
};

export default BusinessDetailsEditor;
